import {
  addCustomIcon,
  defaultStrokeColor,
  mapActive,
} from "../../../../map/utils/map-utils";
import { HlaWay } from "../../../models/HlaWay";
import mapboxgl from "mapbox-gl";
import turf from "../../../../shared/utils/turf";
import cameraIconSvg from "../../../../../assets/svg/camera-icon.svg";
import chevronIconSvg from "../../../../../assets/svg/chevron-icon.svg";

export type CutHlaAnchor = {
  distanceMeters: number;
  point: [number, number];
};

export class CutHlaMapManager {
  map: mapboxgl.Map;
  segment: number[][];
  segmentLength: number;
  anchors: CutHlaAnchor[] = [];
  lineClickListenerRemovers: (() => void)[] = [];
  hoverPoint?: [number, number];
  onAnchorSelected?: (anchor: CutHlaAnchor) => void;

  strokeColor = defaultStrokeColor;

  constructor(map: mapboxgl.Map, segment: number[][]) {
    this.map = map;
    this.segment = segment;
    this.segmentLength = turf.length(turf.lineString(this.segment), {
      units: "meters",
    });

    this.config();
  }

  setOnAnchorSelected(fn: (anchor: CutHlaAnchor) => void) {
    this.onAnchorSelected = fn;
  }

  setMap(map: mapboxgl.Map) {
    this.map = map;
  }

  setAnchors(anchors: CutHlaAnchor[]) {
    this.anchors = anchors;

    if (mapActive(this.map)) {
      this.renderPoints();
    }
  }

  clear() {
    this.lineClickListenerRemovers.map((fn) => fn());
    this.lineClickListenerRemovers = [];

    if (mapActive(this.map)) {
      this.map.removeLayer(strokeLayerConfig.id);
      this.map.removeLayer(lineLayerConfig.id);
      this.map.removeLayer(directionLayerConfig.id);
      this.map.removeSource(segmentSource);

      this.map.removeLayer(pointLayerConfig.id);
      this.map.removeSource(pointSource);
    }

    this.map.remove();
  }

  private config() {
    this.map.on("load", () => {
      this.map.boxZoom.disable();
      this.map.resize();

      this.map.addControl(new mapboxgl.NavigationControl());
      this.map.addControl(
        new mapboxgl.ScaleControl({
          maxWidth: 100,
          unit: "metric",
        }),
      );

      this.map.addSource(segmentSource, sourceConfig);
      this.map.addLayer(strokeLayerConfig);
      this.map.addLayer(lineLayerConfig);
      this.map.addLayer(directionLayerConfig);

      this.map.addSource(pointSource, sourceConfig);
      this.map.addLayer(pointLayerConfig);

      addCustomIcon(this.map, "camera-icon", cameraIconSvg, 30);
      addCustomIcon(this.map, "chevron-icon", chevronIconSvg, 24, true);

      this.renderSegment();

      this.addHoverHandlers();
      this.recenter();
    });
  }

  private renderSegment() {
    const source = this.map.getSource(segmentSource) as mapboxgl.GeoJSONSource;
    source?.setData({
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: this.segment,
          },
        },
      ],
    });
  }

  renderPoints() {
    const toFeature = (
      coordinates: [number, number],
      hover: boolean,
    ): GeoJSON.Feature => ({
      type: "Feature",
      properties: { hover },
      geometry: {
        type: "Point",
        coordinates,
      },
    });

    const features: GeoJSON.Feature[] = this.anchors.map((anchor) =>
      toFeature(anchor.point, false),
    );

    if (this.hoverPoint) {
      features.push(toFeature(this.hoverPoint, true));
    }

    const source = this.map.getSource(pointSource) as mapboxgl.GeoJSONSource;

    source?.setData({
      type: "FeatureCollection",
      features,
    });
  }

  private addHoverHandlers() {
    const canvas = this.map.getCanvasContainer();

    const cursorPointer = () => (canvas.style.cursor = "pointer");
    const cursorDefault = () => (canvas.style.cursor = "");

    const onMouseMove = (event: mapboxgl.MapMouseEvent) => {
      const hover = this.getNearestPoint(event.lngLat);

      this.hoverPoint = hover ? hover.point : undefined;

      this.renderPoints();
    };

    const onPointClick = (e: mapboxgl.MapMouseEvent) => {
      e.preventDefault();
      const hover = this.getNearestPoint(e.lngLat);
      if (!hover) return;
      if (this.onAnchorSelected) {
        this.onAnchorSelected(hover);
      }
    };

    this.map.on("mousemove", onMouseMove);
    this.map.on("mouseenter", pointLayerName, cursorPointer);
    this.map.on("mouseleave", pointLayerName, cursorDefault);
    this.map.on("mousedown", pointLayerName, onPointClick);

    this.lineClickListenerRemovers.push(() => {
      this.map.off("mousemove", onMouseMove);
      this.map.off("mouseenter", pointLayerName, cursorPointer);
      this.map.off("mouseleave", pointLayerName, cursorDefault);
      this.map.off("mousedown", pointLayerName, onPointClick);
    });
  }

  private getNearestPoint(lngLat: mapboxgl.LngLat): CutHlaAnchor | undefined {
    const line = turf.lineString(this.segment);
    const point = turf.point([lngLat.lng, lngLat.lat]);
    const nearest = turf.nearestPointOnLine(line, point, {
      units: "meters",
    });

    if (!nearest || !nearest.geometry || !nearest.properties) {
      return undefined;
    }

    const coordinates = nearest.geometry.coordinates as [number, number];
    const distanceFromStart =
      typeof nearest.properties.location === "number"
        ? nearest.properties.location
        : this.getDistanceAlongLine(line, coordinates);

    if (this.segmentLength === distanceFromStart || 0 === distanceFromStart)
      return;

    return { point: coordinates, distanceMeters: distanceFromStart };
  }

  private getDistanceAlongLine(
    line: GeoJSON.Feature<GeoJSON.LineString>,
    position: [number, number],
  ) {
    const coords = line.geometry.coordinates;
    let distance = 0;

    for (let i = 0; i < coords.length - 1; i++) {
      const start = turf.point(coords[i]);
      const stop = turf.point(coords[i + 1]);
      const segmentDistance = turf.distance(start, stop, { units: "meters" });

      if (
        position[0] === coords[i + 1][0] &&
        position[1] === coords[i + 1][1]
      ) {
        distance += segmentDistance;
        break;
      }

      const alongSegment = turf.nearestPointOnLine(
        turf.lineString([coords[i], coords[i + 1]]),
        turf.point(position),
        { units: "meters" },
      );

      if (
        alongSegment.properties &&
        typeof alongSegment.properties.location === "number"
      ) {
        distance += alongSegment.properties.location;
        break;
      }

      distance += segmentDistance;
    }

    return distance;
  }

  getLineColor(way: HlaWay) {
    var color = "#FFFFFF";
    const frontScore = way.frontFacingLane?.score?.global;
    const backScore = way.backFacingLane?.score?.global;

    var worstScore = frontScore;

    if (
      backScore != null &&
      (worstScore == null || worstScore.value < backScore.value)
    ) {
      worstScore = backScore;
    }

    if (worstScore) {
      color = worstScore.color;
    }
    return color;
  }

  disableLineClick() {
    this.lineClickListenerRemovers.map((fn) => fn());
    this.lineClickListenerRemovers = [];
  }

  recenter() {
    const bounds = new mapboxgl.LngLatBounds();

    this.segment.forEach((coords) => {
      bounds.extend(coords as [number, number]);
    });

    this.map.fitBounds(bounds, {
      padding: 50,
      duration: 1000,
      maxZoom: 18,
    });
  }
}

const isLineString = ["==", "$type", "LineString"];

const segmentSource = "cutHlaSegment";
const pointSource = "cutHlaPoint";
const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: {
    type: "FeatureCollection",
    features: [],
  },
};

const lineLayerName = `${segmentSource}-line`;
const lineLayerConfig: mapboxgl.LineLayerSpecification = {
  id: lineLayerName,
  source: segmentSource,
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": "#fff",
    "line-width": 5,
  },
  filter: isLineString,
};

const strokeLayerName = `${segmentSource}-stroke`;
const strokeLayerConfig: mapboxgl.LineLayerSpecification = {
  id: strokeLayerName,
  source: segmentSource,
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": defaultStrokeColor,
    "line-width": 11,
  },
  filter: isLineString,
};

const isHover = ["boolean", ["get", "hover"], false];

const pointLayerName = `${segmentSource}-hover-point`;
const pointLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: pointLayerName,
  source: pointSource,
  type: "circle",
  paint: {
    "circle-color": ["case", isHover, "#2cc1ad", "#167e70"],
    "circle-radius": 8,
    "circle-stroke-color": "#ffffff",
    "circle-stroke-width": 2,
  },
  filter: ["==", "$type", "Point"],
};

// Adds arrows to the way lines so that you know the direction
const directionLayerName = `${segmentSource}-direction`;
const directionLayerConfig: mapboxgl.SymbolLayerSpecification = {
  id: directionLayerName,
  source: segmentSource,
  type: "symbol",
  layout: {
    "symbol-placement": "line",
    "symbol-spacing": 50,
    "icon-image": "chevron-icon",
    "icon-rotation-alignment": "map",
    "icon-size": 0.5,
  },
  paint: {
    "icon-color": defaultStrokeColor,
  },
};

export const hlaWayShiftDragLayerName = lineLayerName;
